import styled from "styled-components";
import React, { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from "@material-ui/core";
import { InertiaLink, usePage } from "@inertiajs/inertia-react";
import { getCookieValue } from "../utils/getCookieValue";
import { Trans } from "../utils/i18n";
import DeleteIcon from "@material-ui/icons/Delete";
import Grid from "@material-ui/core/Grid";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MuiSelect from "@material-ui/core/Select";
import FormHelperText from "@material-ui/core/FormHelperText";

export default function QueueDetailDropzone({
    enableType,
}) {
    const { files: lol, order, } = usePage().props;
    const userCanView = order.permissions?.file?.viewAny;
    const userCanCreate = order.permissions?.file?.create;
    const userCanDelete = order.permissions?.file?.delete;
    const [status, setStatus] = useState("initial"); // initial | validating | valid | uploading | uploaded | upload-failed
    const [responseMessage, setResponseMessage] = useState("");
    const [errors, setErrors] = useState([]);
    const [fileType, setFileType] = useState(enableType ? "" : 'other');
    const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
        // maxFiles: 1,
        disabled: fileType === "",
    });
    const [files, setFiles] = useState(
        lol.map((item) => ({
            serverResponse: item,
            fileName: "",
            previewImage: "",
        }))
    );
    const [dialogOpen, setDialogOpen] = React.useState(false);

    const handleClose = () => {
        setDialogOpen(false);
    };

    const handleConfirmDelete = () => {
        fetch(route("file.delete", dialogOpen.serverResponse.id), {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
                "X-XSRF-TOKEN": getCookieValue("XSRF-TOKEN"),
                "X-Requested-With": "XMLHttpRequest",
            },
        })
            .then((response) => response.json())
            .then((data) => {
                if (!data.success) {
                    throw "error";
                }

                const foundIndex = files.findIndex(
                    (item) =>
                        item.serverResponse.id === dialogOpen.serverResponse.id
                );

                setFiles((prev) =>
                    prev.filter((file, index) => index !== foundIndex)
                );
                setDialogOpen(false);
            })
            .catch((data) => {
                //TODO: Display error message
            });
    };

    useEffect(() => {
        const asyncFunction = async (droppedFile) => {
            try {
                const formData = new FormData();
                formData.append("file", droppedFile);
                formData.append("type", fileType);
                formData.append("order_id", order.order_id);

                setStatus("validating");
                const uploadResponse = await fetch(route("file.upload"), {
                    headers: {
                        "X-XSRF-TOKEN": getCookieValue("XSRF-TOKEN"),
                        "X-Requested-With": "XMLHttpRequest",
                    },
                    method: "POST",
                    body: formData,
                });

                const { message, errors, file } = await uploadResponse.json();

                if (uploadResponse.status === 200) {
                    setStatus("valid");
                    setErrors([]);
                    const newImage = URL.createObjectURL(droppedFile);

                    setFiles((prev) => [
                        ...prev,
                        {
                            serverResponse: file,
                            fileName: droppedFile.path,
                            previewImage: newImage,
                        },
                    ]);
                    return;
                }
                setStatus("initial");
                setResponseMessage(message);
                if (errors) {
                    setErrors(errors);
                }
            } catch (error) {
                setStatus("initial");
                setResponseMessage(
                    <Trans i18nKey="dropzone.errorResponseMessage">
                        An error occurred. Please try again!
                    </Trans>
                );
                setErrors([]);
            }
        };

        acceptedFiles.forEach(asyncFunction);
    }, [acceptedFiles]);

    return (
        <div>
            <StyledDropzone>
                {userCanCreate && (
                    <>
                        {enableType && (
                            <FormControl variant="outlined">
                                <InputLabel htmlFor={"selectfiletype"}>
                                    File type
                                </InputLabel>
                                <MuiSelect
                                    name={"selectfiletype"}
                                    id={"selectfiletype"}
                                    native
                                    label={"File type"}
                                    onChange={(event) =>
                                        setFileType(event.target.value)
                                    }
                                    value={fileType}
                                >
                                    <option aria-label={<Trans i18nKey="select.none">None</Trans>} value=""/>
                                    {[
                                        {
                                            value: "problem",
                                            label: "Problem",
                                        },
                                        {
                                            value: "receipt",
                                            label: "Receipt",
                                        },
                                        {
                                            value: "returnlabel",
                                            label: "Return label",
                                        },
                                        {
                                            value: "shippinglabel",
                                            label: "Shipping label",
                                        },
                                        {
                                            value: "other",
                                            label: "Other",
                                        },
                                    ].map((item, index) => (
                                        <option key={index} value={item.value}>
                                            {item.label}
                                        </option>
                                    ))}
                                </MuiSelect>
                            </FormControl>
                        )}
                        <div {...getRootProps({ className: "dropzone" })}>
                            <input {...getInputProps()} />
                            <StyledDropField status={status}>
                                {
                                    fileType !== "" ? (
                                        <Trans i18nKey="dropzone.dropFieldStatus">
                                            Drag 'n' drop one file here, or click to
                                            select a file
                                        </Trans>
                                    ) : (
                                        <Trans i18nKey="dropzone.pleaseSelectAType">
                                            Please select a file type first before uploading a file.
                                        </Trans>
                                    )
                                }
                            </StyledDropField>
                        </div>
                    </>
                )}
                {userCanView && (
                    <>
                        <StyledFilename>
                            <Trans i18nKey="dropzone.filename">
                                Filename:{" "}
                            </Trans>
                        </StyledFilename>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                {files &&
                                    files.map((item, key) => (
                                        <Grid
                                            container
                                            spacing={3}
                                            key={item.fileName}
                                        >
                                            <Grid item xs={2}>
                                                {item.serverResponse
                                                        .mimetype ===
                                                    "application/pdf" && (
                                                        <PreviewImageSVG
                                                            aria-hidden="true"
                                                            focusable="false"
                                                            data-prefix="fas"
                                                            data-icon="file-pdf"
                                                            role="img"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            viewBox="0 0 384 512"
                                                            className="svg-inline--fa fa-file-pdf fa-w-12 fa-3x"
                                                        >
                                                            <path
                                                                fill="currentColor"
                                                                d="M181.9 256.1c-5-16-4.9-46.9-2-46.9 8.4 0 7.6 36.9 2 46.9zm-1.7 47.2c-7.7 20.2-17.3 43.3-28.4 62.7 18.3-7 39-17.2 62.9-21.9-12.7-9.6-24.9-23.4-34.5-40.8zM86.1 428.1c0 .8 13.2-5.4 34.9-40.2-6.7 6.3-29.1 24.5-34.9 40.2zM248 160h136v328c0 13.3-10.7 24-24 24H24c-13.3 0-24-10.7-24-24V24C0 10.7 10.7 0 24 0h200v136c0 13.2 10.8 24 24 24zm-8 171.8c-20-12.2-33.3-29-42.7-53.8 4.5-18.5 11.6-46.6 6.2-64.2-4.7-29.4-42.4-26.5-47.8-6.8-5 18.3-.4 44.1 8.1 77-11.6 27.6-28.7 64.6-40.8 85.8-.1 0-.1.1-.2.1-27.1 13.9-73.6 44.5-54.5 68 5.6 6.9 16 10 21.5 10 17.9 0 35.7-18 61.1-61.8 25.8-8.5 54.1-19.1 79-23.2 21.7 11.8 47.1 19.5 64 19.5 29.2 0 31.2-32 19.7-43.4-13.9-13.6-54.3-9.7-73.6-7.2zM377 105L279 7c-4.5-4.5-10.6-7-17-7h-6v128h128v-6.1c0-6.3-2.5-12.4-7-16.9zm-74.1 255.3c4.1-2.7-2.5-11.9-42.8-9 37.1 15.8 42.8 9 42.8 9z"
                                                                className=""
                                                            ></path>
                                                        </PreviewImageSVG>
                                                    )}
                                                {item.serverResponse
                                                        .mimetype !==
                                                    "application/pdf" && (
                                                        <PreviewImage
                                                            src={route(
                                                                "file.download",
                                                                {
                                                                    file_id:
                                                                    item
                                                                        .serverResponse
                                                                        .id,
                                                                }
                                                            )}
                                                            alt={"Preview image"}
                                                        />
                                                    )}
                                            </Grid>
                                            <Grid item xs={6}>
                                                <a
                                                    href={route(
                                                        "file.download",
                                                        {
                                                            file_id:
                                                            item
                                                                .serverResponse
                                                                .id,
                                                        }
                                                    )}
                                                    title={"Download file"}
                                                >
                                                    {item.serverResponse.name}
                                                </a>
                                                <br />
                                                <Trans
                                                    i18nKey={`queueDetail.fileType.${item?.serverResponse?.type}`}
                                                ></Trans>
                                            </Grid>
                                            {userCanDelete && (
                                                <Grid item xs={4}>
                                                    <Button
                                                        variant="contained"
                                                        color="secondary"
                                                        startIcon={
                                                            <DeleteIcon />
                                                        }
                                                        onClick={() => {
                                                            setDialogOpen(item);
                                                        }}
                                                    >
                                                        Delete
                                                    </Button>
                                                </Grid>
                                            )}
                                        </Grid>
                                    ))}
                            </Grid>
                        </Grid>
                    </>
                )}
                {["validating", "uploading"].includes(status) && (
                    <StyledCircularProgress size={25} />
                )}
                {["initial", "valid"].includes(status) && (
                    <h4>{responseMessage}</h4>
                )}
                <ul>
                    {errors.map &&
                        errors.map((item) => (
                            <li>
                                <Trans i18nKey="dropzone.row">Row</Trans>{" "}
                                {item.row}
                                <ul>
                                    {item.errors &&
                                        item.errors.map((item) => (
                                            <li>{item}</li>
                                        ))}
                                </ul>
                            </li>
                        ))}
                    {!errors.map &&
                        Object.values(errors).map((item) => (
                            <li>{item && item.join(", ")}</li>
                        ))}
                </ul>
            </StyledDropzone>
            <Dialog
                open={dialogOpen}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    <Trans i18nKey="queueDetail.deleteDialog.title">
                        Do you really want to delete the file?
                    </Trans>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        <p>
                            <Trans i18nKey="queueDetail.deleteDialog.filename">
                                Filename:{" "}
                            </Trans>{" "}
                            {dialogOpen?.serverResponse?.name}
                        </p>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={handleClose}>
                        <Trans i18nKey="queueDetail.deleteDialog.abort">
                            Abort
                        </Trans>
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleConfirmDelete}
                        autoFocus
                    >
                        <Trans i18nKey="queueDetail.deleteDialog.confirm">
                            Delete
                        </Trans>
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

const PreviewImage = styled.img`
    height: 70px;
    width: 70px;
    object-fit: cover;
`;

const PreviewImageSVG = styled.svg`
    height: 50px;
    width: 50px;
    object-fit: cover;
`;

const StyledDropzone = styled.div`
    marginbottom: 15px;
`;

const StyledDropField = styled.p`
    padding: 100px 50px;
    border: 2px dashed currentColor;
    text-align: center;
    opacity: ${({ status }) =>
    ["uploading"].includes(status) ? "50%" : "100%"};
    cursor: ${({ status }) =>
    ["uploading"].includes(status) ? "not-allowed" : "pointer"};
`;

const StyledFilename = styled.h4`
    display: inline-block;
    margin-right: 30px;
    vertical-align: middle;
`;

const StyledCircularProgress = styled(CircularProgress)`
    vertical-align: middle;
`;

const StyledButtons = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 30px;
    margin: 30px 0;
`;

const StyledLink = styled(InertiaLink)`
    text-decoration: none;
`;
